import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle, Loader2 } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Seo from '../components/Seo';
import { pickMeta } from '../seoMeta';

function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(t('auth.resetLinkSent') || 'Ссылка для сброса пароля отправлена');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.resetError') || 'Ошибка отправки');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {(() => { const m = pickMeta('forgotPassword', currentLang); return <Seo lang={currentLang} path="forgot-password" title={m.title} description={m.description} />; })()}
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={`/${currentLang}`} className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="Ainanan" 
              className="h-10 object-contain"
            />
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Link to={`/${currentLang}/login`} className="inline-flex items-center text-stone-600 hover:text-stone-800 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('auth.backToLogin') || 'Назад к входу'}
          </Link>

          <div className="bg-white rounded-xl shadow-sm border p-8">
            {!sent ? (
              <>
                <div className="text-center mb-6">
                  <Mail className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('auth.forgotPasswordTitle') || 'Забыли пароль?'}
                  </h1>
                  <p className="text-gray-500">
                    {t('auth.forgotPasswordDesc') || 'Введите email и мы отправим ссылку для сброса пароля'}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-gray-700">{t('auth.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="your@email.com"
                      className="mt-1"
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5"
                    data-testid="forgot-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('auth.sending') || 'Отправка...'}
                      </>
                    ) : (
                      t('auth.sendResetLink') || 'Отправить ссылку'
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {t('auth.checkEmail') || 'Проверьте почту'}
                </h2>
                <p className="text-gray-500 mb-6">
                  {t('auth.resetLinkSentTo') || 'Ссылка для сброса пароля отправлена на'}{' '}
                  <span className="font-medium text-gray-700">{email}</span>
                </p>
                <p className="text-sm text-gray-400 mb-6">
                  {t('auth.checkSpam') || 'Не забудьте проверить папку "Спам"'}
                </p>
                <Button
                  onClick={() => setSent(false)}
                  variant="outline"
                  className="w-full"
                >
                  {t('auth.tryAnotherEmail') || 'Попробовать другой email'}
                </Button>
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {t('auth.rememberPassword') || 'Вспомнили пароль?'}{' '}
                <Link to={`/${currentLang}/login`} className="font-medium text-emerald-700 hover:text-emerald-800">
                  {t('auth.loginLink')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default ForgotPassword;
